/**
 * session-title — compute a coaching session's display name.
 *
 * Sessions no longer store an auto-generated `"{coach} / {client}"` title: a
 * session's `title` column is `null` unless the coach typed one. This helper is
 * the single source of truth for what to render when the stored title is empty,
 * so the old ad-hoc fallbacks (`title ?? user.email.toLowerCase()`,
 * `title ?? "Coaching session"`) can be retired.
 *
 * Aliases: session display name, session title fallback, session label, session name.
 */

/**
 * Resolve the display title for a session.
 *
 * Resolution order:
 * 1. the stored `title`, when non-empty;
 * 2. `Session {sessionNumber}` (with ` · {counterpartName}` appended when a
 *    counterpart is provided) when a chronological number is available;
 * 3. `counterpartName` alone when there is no number but a counterpart;
 * 4. `"Coaching session"` as a last resort.
 *
 * @param title - stored session title (may be `null`/`undefined`/empty).
 * @param sessionNumber - 1-based chronological position within the engagement.
 * @param counterpartName - the other party's name. On coach cross-client
 *   surfaces (e.g. the daily agenda) this is the client's name; engagement-scoped
 *   and client surfaces omit it so the session reads simply as `Session {n}`.
 * @returns a non-empty display string, never `null`.
 */
export function sessionDisplayTitle({
  title,
  sessionNumber,
  counterpartName,
}: {
  title: string | null | undefined;
  sessionNumber?: number | null;
  counterpartName?: string | null;
}): string {
  if (title) return title;

  const name = counterpartName || undefined;
  const hasNumber =
    typeof sessionNumber === "number" && Number.isFinite(sessionNumber);

  if (hasNumber) {
    return name
      ? `Session ${sessionNumber} · ${name}`
      : `Session ${sessionNumber}`;
  }

  return name ?? "Coaching session";
}
